import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navItems = [
    { key: 'nav.home', path: '/' },
    { key: 'nav.media', path: '/media' },
    { key: 'nav.videos', path: '/videos' },
  ];

  const switchLang = (lang: string) => {
    if (i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  };

  const handleNav = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
    window.scrollTo(0, 0);
  };

  const navBg = isScrolled
    ? 'rgba(248,243,234,0.98)'
    : 'rgba(248,243,234,0.92)';

  const navText = '#082B5F';
  const navTextMuted = 'rgba(8,43,95,0.60)';
  const navBorder = 'rgba(8,43,95,0.08)';
  const gold = '#C8A45D';
  const isZh = i18n.language === 'zh';

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          background: navBg,
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          borderBottom: `1px solid ${navBorder}`,
          boxShadow: isScrolled
            ? '0 4px 18px rgba(8,43,95,0.06)'
            : '0 4px 18px rgba(8,43,95,0.04)',
        }}
      >
        <div className="w-full px-5 md:px-10 lg:px-14">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Logo */}
            <div
              className="flex items-center gap-3 cursor-pointer group"
              onClick={() => handleNav('/')}
            >
              <div className="w-[72px] h-[72px] md:w-[88px] md:h-[88px] flex items-center justify-center transition-transform duration-300 group-hover:scale-105">
                <img
                  src="https://storage.readdy-site.link/project_files/24d9f673-4dd5-4514-9bb2-bf7280a460c7/8955655c-e53a-4719-b771-f2c96d3769bf_4----Photoroom.png?v=a73ad237787a5912840cf1a463cb272f"
                  alt="Rotary International Peace Monument"
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden md:block">
                <h1 className="text-sm font-bold leading-tight tracking-wide" style={{ color: navText }}>
                  {isZh ? '國際扶輪和平意象' : 'Rotary International Peace Monument'}
                </h1>
                <p className="text-[11px] tracking-wider" style={{ color: navText, opacity: 0.58 }}>
                  {isZh ? '澎湖和平意象紀念碑' : 'Penghu Peace Monument'}
                </p>
              </div>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-8">
              {navItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNav(item.path)}
                    className="relative text-sm font-medium tracking-wide transition-colors duration-300 whitespace-nowrap py-1"
                    style={
                      isActive
                        ? { color: navText, fontWeight: 700 }
                        : { color: navTextMuted }
                    }
                    onMouseEnter={(e) => {
                      (e.target as HTMLElement).style.color = navText;
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) {
                        (e.target as HTMLElement).style.color = navTextMuted;
                      }
                    }}
                  >
                    {t(item.key)}
                    <span
                      className="absolute left-0 -bottom-0.5 h-[2px] transition-all duration-300 rounded-full"
                      style={{
                        width: isActive ? '100%' : '0%',
                        background: gold,
                        opacity: 0.85,
                      }}
                    />
                  </button>
                );
              })}
              <div className="w-px h-4" style={{ background: 'rgba(8,43,95,0.12)' }} />

              {/* Language Switcher - Capsule Style */}
              <div
                className="flex items-center rounded-full overflow-hidden"
                style={{
                  border: '1px solid rgba(8,43,95,0.18)',
                  background: 'rgba(248,243,234,0.6)',
                }}
              >
                <button
                  onClick={() => switchLang('en')}
                  className="text-sm font-medium transition-all duration-300 whitespace-nowrap px-3.5 py-1.5"
                  style={{
                    background: isZh ? 'transparent' : '#082B5F',
                    color: isZh ? navTextMuted : '#FFFFFF',
                    fontWeight: isZh ? 500 : 700,
                  }}
                  onMouseEnter={(e) => {
                    const el = e.target as HTMLElement;
                    if (isZh) {
                      el.style.color = navText;
                      el.style.background = 'rgba(200,164,93,0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const el = e.target as HTMLElement;
                    if (isZh) {
                      el.style.color = navTextMuted;
                      el.style.background = 'transparent';
                    }
                  }}
                >
                  EN
                </button>
                <div
                  className="w-px h-3.5"
                  style={{ background: 'rgba(8,43,95,0.15)' }}
                />
                <button
                  onClick={() => switchLang('zh')}
                  className="text-sm font-medium transition-all duration-300 whitespace-nowrap px-3.5 py-1.5"
                  style={{
                    background: isZh ? '#082B5F' : 'transparent',
                    color: isZh ? '#FFFFFF' : navTextMuted,
                    fontWeight: isZh ? 700 : 500,
                  }}
                  onMouseEnter={(e) => {
                    const el = e.target as HTMLElement;
                    if (!isZh) {
                      el.style.color = navText;
                      el.style.background = 'rgba(200,164,93,0.12)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    const el = e.target as HTMLElement;
                    if (!isZh) {
                      el.style.color = navTextMuted;
                      el.style.background = 'transparent';
                    }
                  }}
                >
                  中文
                </button>
              </div>
            </div>

            {/* Mobile Hamburger */}
            <button
              className="md:hidden w-10 h-10 flex items-center justify-center"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              <i
                className="ri-menu-3-line text-2xl"
                style={{ color: navText }}
              />
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 md:hidden ${
          mobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        style={{
          background: 'rgba(248,243,234,0.98)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
        }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => handleNav(item.path)}
                className="text-2xl font-heading font-medium transition-colors relative"
                style={
                  isActive
                    ? { color: navText, fontWeight: 700 }
                    : { color: navTextMuted }
                }
              >
                {t(item.key)}
                {isActive && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 -bottom-2 h-[2px] w-8 rounded-full"
                    style={{ background: gold, opacity: 0.85 }}
                  />
                )}
              </button>
            );
          })}
          <div className="w-12 h-px" style={{ background: navBorder }} />

          {/* Mobile Language Switcher - Capsule Style */}
          <div
            className="flex items-center rounded-full overflow-hidden"
            style={{
              border: '1px solid rgba(8,43,95,0.18)',
              background: 'rgba(248,243,234,0.6)',
            }}
          >
            <button
              onClick={() => switchLang('en')}
              className="text-lg font-medium transition-all duration-300 whitespace-nowrap px-5 py-2"
              style={{
                background: isZh ? 'transparent' : '#082B5F',
                color: isZh ? navTextMuted : '#FFFFFF',
                fontWeight: isZh ? 500 : 700,
              }}
            >
              EN
            </button>
            <div
              className="w-px h-4"
              style={{ background: 'rgba(8,43,95,0.15)' }}
            />
            <button
              onClick={() => switchLang('zh')}
              className="text-lg font-medium transition-all duration-300 whitespace-nowrap px-5 py-2"
              style={{
                background: isZh ? '#082B5F' : 'transparent',
                color: isZh ? '#FFFFFF' : navTextMuted,
                fontWeight: isZh ? 700 : 500,
              }}
            >
              中文
            </button>
          </div>

          <button
            className="mt-8 w-12 h-12 flex items-center justify-center"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          >
            <i className="ri-close-line text-3xl" style={{ color: navText }} />
          </button>
        </div>
      </div>
    </>
  );
}